import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, TimeoutError } from 'rxjs';
import { SiteState, SiteControl, SiteConfig, EvseConfig, OverrideSettings, EvseSettings, EvseCommand, LatLong, AppSettings, ChargingSchedulePeriod, SolarCredentials } from '../shared/db_types';
import { UpdateAmberApiTokenRequest, SuggestedOcppIdReply, CreateEvseRequest, GetTeslaAccountReply, SelectTeslaRequest, AddTeslaRequest, UpdateCountryRequest, GetAccountReply, UpdateAccountRequest, UpdateEmailRequest, StatsViewRequest, StatsViewReply, StatsSummaryReply, SelectEnergySiteRequest, FcmActionReport, AppOpenedRequest, CreateCheckoutLinkReply, CreateCheckoutLinkRequest, CreateStripeLinkReply, GetSubscriptionReply, GetSiteFullReply, GetMyPlansReply, StartFreeTrialRequest, AppVariant, AppVersionInfo, ChangeSubscriptionIntervalRequest, NoticeDetailsReply, AcceptNoticeReply, API_VERSION, GetScheduleReply, SendAlphaEssCodeRequest, ConnectTeslaRequest, ConnectTeslaReply, GetTeslaPairingReply, GetConnectTeslaReply, GetRevokeTeslaAuthReply, DeleteTeslaReply, ReplyBase, TestTeslaPairingReply, NamedAction, ChangeEmailReply, DataDownloadPageReply, DownloadDataRequest } from '../shared/api_types'
import { AlertController, Platform } from '@ionic/angular';
import { AuthService, getAuthToken } from './auth.service';
import { getApiUrl } from '../shared/app_helpers';
import { DateTime } from 'luxon';
import { ConnectionState } from 'src/shared/tesla_types';

const SOCKETIO_URL = getApiUrl()
const API_URL = SOCKETIO_URL + '/api';

function logTs(): string {
  return DateTime.now().toISOTime()
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private _paused = false
  private socket: Socket;
  private _siteState: SiteState
  private _isVehicleWakingUp = false
  public siteStateStream: BehaviorSubject<SiteState> = new BehaviorSubject(null);

  constructor(private http: HttpClient,
    private platform: Platform,
    private authService: AuthService,
    private alertController: AlertController) {
      console.debug(`ApiService - using url ${SOCKETIO_URL}`)
      this.authService.isLoggedIn.subscribe({
        next: v => {
          if (v) {
            console.debug('ApiService - now logged in, opening websocket')
            this.disconnectSocket() //in case user switched due to impersonation
            this.updateSiteState(undefined)
            this.connectSocket(getAuthToken())
          }
          else {
            console.debug('ApiService - logged out, closing websocket')
            this.updateSiteState(undefined)
            this.disconnectSocket()
          }
        }
      })
      // when the app is put in background then close the websocket as we don't need the feeds
      this.platform.pause.subscribe(() => {
        this._paused = true
        if (this.socket) {
          console.debug(`ApiService - app paused ${logTs()} - closing websocket`)
          this.disconnectSocket()
        }
        else {
          console.debug(`ApiService - app paused ${logTs()} - websocket is not connected`)
        }
      })
      // when resuming from background on a device there is a delay reconnecting
      // the websocket, so force it to happen immediately
      this.platform.resume.subscribe(() => {
        this._paused = false
        if (this.authService.hasToken()) {
          console.debug(`ApiService - app resumed ${logTs()} - reconnecting websocket`)
          this.connectSocket(getAuthToken())
        }
        else {
          console.debug('ApiService - app resumed, not logged in, not reconnecting websocket')
        }
      })
  }

  // are we running on a mobile device? (including a mobile web browser)
  get isMobileDevice(): boolean {
    return this.platform.is('mobile')
  }

  // are we running as a mobile app?
  get isMobileApp(): boolean {
    return this.platform.is('hybrid')
  }

  // the last received site state
  get siteState(): SiteState | undefined {
    return this._siteState
  }

  get teslaLocation(): LatLong | undefined {
    if (this.siteState?.teslaState?.fields?.latitude && this.siteState?.teslaState?.fields?.longitude) {
      return {
        lat: this.siteState.teslaState.fields.latitude,
        long: this.siteState.teslaState.fields.longitude,
      }
    }
    else {
      return undefined
    }
  }

  // is the app in the background (on devices)
  get paused() {
    return this._paused
  }

  private updateSiteState(siteState: SiteState) {
    this._siteState = siteState
    // if we are waking the vehicle and it is now online, clear the waking state
    if (this.isVehicleWakingUp && this.isVehicleOnline) {
      console.debug('wake up successful')
      this._isVehicleWakingUp = false
    }
    this.siteStateStream.next(siteState)
  }

  // (re)connect the socket.io socket, given the access token
  private connectSocket(token: string) {
    if (this.socket) {
      console.debug('ApiService - disconnecting websocket')
      this.socket.disconnect()
      this.socket = undefined
    }
    console.debug(`ApiService - creating socket.io socket ${logTs()}`)
    this.socket = io(SOCKETIO_URL, {
      query: {
        token,
        apiVersion: API_VERSION.toString()
      },
      transports: ["websocket", "polling"] // use websocket first, to speed up (re)connection
    });
    this.socket.on('connect', () => {
      console.debug(`socket.io connected ${logTs()}`)
    })
    this.socket.on('disconnect', (reason) => {
      console.debug(`socket.io disconnected: ${reason} ${logTs()}`)
    })
    this.socket.on('connect_error', (err) => {
      console.error(`socket.io connect_error ${err} ${logTs()}`)
    })
    this.socket.on('site-state', (v: SiteState) => {
      console.debug('RX site-state', logTs(), v);
      // ignore the null we seem to get on first connect
      if (v) {
        this.updateSiteState(v)
      }
    });
    this.socket.connect();
  }

  private disconnectSocket() {
    if (this.socket) {
      console.debug('ApiService - disconnecting socket.io socket')
      this.socket.disconnect()
      this.socket = null
    }
  }

  async getPrivate() {
    console.debug('getting /private')
    const res = await this.http.get(`${API_URL}/private`).toPromise();
    console.debug(res);
  }

  async postReportAppOpened(body: AppOpenedRequest) {
    console.debug('posting /app-opened', JSON.stringify(body))
    const res = await this.http.post(`${API_URL}/app-opened`, body).toPromise()
    return res
  }

  async getAccount(): Promise<GetAccountReply> {
    const res = await this.http.get(`${API_URL}/account`).toPromise()
    return res as GetAccountReply
  }

  async getMyPlans(): Promise<GetMyPlansReply> {
    const res = await this.http.get(`${API_URL}/my-plans`).toPromise()
    return res as GetMyPlansReply
  }

  async getSubscription(): Promise<GetSubscriptionReply> {
    const res = await this.http.get(`${API_URL}/subscription`).toPromise()
    return res as GetSubscriptionReply
  }

  async postUpdateAppSettings(body: Partial<AppSettings>) {
    console.debug('posting /update-app-settings', body)
    const res = await this.http.post(`${API_URL}/update-app-settings`, body).toPromise()
    return res
  }

  async postUpdateAccount(body: UpdateAccountRequest) {
    console.debug('posting /update-account', body)
    const res = await this.http.post(`${API_URL}/update-account`, body).toPromise()
    return res
  }

  async requestChangeEmailCode(email: string) {
    const url = '/request-change-email-code'
    console.debug('posting', url, email)
    const res = await this.http.post(`${API_URL}${url}`, { email }).toPromise()
    return res
  }

  async postDeleteAccount() {
    console.debug('posting /delete-account')
    const res = await this.http.post(`${API_URL}/delete-account`, {}).toPromise()
    return res
  }

  async getSite(): Promise<SiteConfig> {
    const res = await this.http.get(`${API_URL}/site`).toPromise();
    //console.debug('got site', res)
    return res as SiteConfig;
  }

  async getSiteFull(): Promise<GetSiteFullReply> {
    const res = await this.http.get(`${API_URL}/site-full`, {
      params: {
        apiVersion: API_VERSION,
      }
    }).toPromise()
    return res as GetSiteFullReply
  }

  async getEvse(): Promise<EvseConfig> {
    const res = await this.http.get(`${API_URL}/evse`).toPromise();
    return res as EvseConfig
  }

  async getSuggestedOcppId() {
    const res = await this.http.get(`${API_URL}/config/suggested-ocppid`).toPromise()
    return res as SuggestedOcppIdReply
  }

  async postCreateEvse(config: CreateEvseRequest) {
    const body = config
    console.debug('posting /config/evses', body);
    const res = await this.http.post(`${API_URL}/config/create-evse`, body).toPromise();
    return res;
  }

  async postUpdateEvseSettings(settings: Partial<EvseSettings>) {
    const body = settings
    const url = `${API_URL}/config/update-evse`;
    console.debug(`posting ${url}`, body);
    const res = await this.http.post(url, body).toPromise();
    return res as SiteConfig;
  }

  async deleteEvse() {
    const url = `${API_URL}/config/evse`;
    console.debug(`deleting ${url}`);
    const res = await this.http.delete(url).toPromise();
    return res
  }

  async sendEvseCommand(command: EvseCommand) {
    const body = command
    const url = `${API_URL}/evse/command`;
    console.debug(`posting ${url}`, body);
    const res = await this.http.post(url, body).toPromise();
    return res
  }

  async postAddEnergyMonitor(credentials: SolarCredentials) {
    const body = credentials
    console.debug('posting /config/energy-monitor', body)
    const res = await this.http.post(`${API_URL}/config/energy-monitor`, body).toPromise()
    return res as SiteConfig
  }

  async postSendAlphaEssCode(sysSn: string, checkCode: string) {
    const body: SendAlphaEssCodeRequest = { sysSn, checkCode }
    console.debug('posting /config/send-alphaess-code', body)
    const res = await this.http.post(`${API_URL}/config/send-alphaess-code`, body).toPromise()
    return res
  }

  async postSelectEnergySite(energySiteId: string) {
    const body: SelectEnergySiteRequest = { energySiteId }
    console.debug('posting /config/select-energy-site', body)
    const res = await this.http.post(`${API_URL}/config/select-energy-site`, body).toPromise()
    return res
  }

  async deleteEnergyMonitor() {
    const url = `${API_URL}/config/energy-monitor`;
    console.debug(`deleting ${url}`);
    const res = await this.http.delete(url).toPromise();
    return res
  }

  async getConnectTeslaReply(): Promise<GetConnectTeslaReply> {
    const res = await this.http.get(`${API_URL}/config/connect-tesla`).toPromise()
    return res as GetConnectTeslaReply
  }

  async postConnectTeslaAccount(body: ConnectTeslaRequest): Promise<ConnectTeslaReply> {
    console.debug('posting /config/connect-tesla-account', body);
    const res = await this.http.post(`${API_URL}/config/connect-tesla-account`, body).toPromise()
    return res as ConnectTeslaReply
  }

  async getTeslaAccount(): Promise<GetTeslaAccountReply> {
    const res = await this.http.get(`${API_URL}/config/tesla-account`).toPromise();
    return res as GetTeslaAccountReply
  }

  async getTeslaPairing(): Promise<GetTeslaPairingReply> {
    const res = await this.http.get(`${API_URL}/config/tesla-pairing`).toPromise()
    return res as GetTeslaPairingReply
  }

  async postTestTeslaPairing(): Promise<TestTeslaPairingReply> {
    const res = await this.http.post(`${API_URL}/config/test-tesla-pairing`, {}).toPromise()
    return res as TestTeslaPairingReply
  }

  async getRevokeTeslaAuth(): Promise<GetRevokeTeslaAuthReply> {
    const res = await this.http.get(`${API_URL}/config/revoke-tesla-auth`).toPromise()
    return res as GetRevokeTeslaAuthReply
  }

  async postSelectTesla(body: SelectTeslaRequest) {
    console.debug('posting /config/select-tesla', body);
    const res = await this.http.post(`${API_URL}/config/select-tesla`, body).toPromise();
    return res
  }

  async putTeslaAccount(tokens: AddTeslaRequest) {
    const body = tokens
    console.debug('putting /config/tesla-account', body);
    const res = await this.http.put(`${API_URL}/config/tesla-account`, body).toPromise();
    return res
  }

  async postSendTeslaPairingLink() {
    const body = {}
    const url = 'config/send-tesla-pairing-link'
    console.debug('post', url, body)
    const res = await this.http.post(`${API_URL}/${url}`, body).toPromise()
    return res
  }

  async deleteTeslaAccount(): Promise<DeleteTeslaReply> {
    console.debug('deleting /config/tesla-account');
    const res = await this.http.delete(`${API_URL}/config/tesla-account`).toPromise();
    return res as DeleteTeslaReply
  }

  async getSiteSettings(): Promise<SiteControl> {
    const res = await this.http.get(`${API_URL}/site-settings`).toPromise();
    //console.debug('got site-settings', res);
    return res as SiteControl;
  }

  async postUpdateSiteSettings(settings: Partial<SiteControl>): Promise<SiteControl> {
    console.debug('posting /update-site-settings', settings);
    const res = await this.http.post(`${API_URL}/update-site-settings`, settings).toPromise();
    console.debug(res);
    return res as SiteControl;
  }

  async postOverrideSettings(settings: OverrideSettings) {
    console.debug('posting /override-settings', settings);
    const res = await this.http.post(`${API_URL}/override-settings`, settings).toPromise();
    console.debug(res);
    return res
  }

  async postUpdateCountry(req: UpdateCountryRequest) {
    console.debug('posting /update-country', req)
    const res = await this.http.post(`${API_URL}/update-country`, req).toPromise();
    console.debug(res);
    return res
  }

  async getSchedule() {
    const res = await this.http.get(`${API_URL}/schedule`).toPromise()
    return res as GetScheduleReply
  }

  async postToggleSchedule(enabled: boolean) {
    console.debug('posting /toggle-schedule', enabled)
    const res = await this.http.post(`${API_URL}/toggle-schedule`, { enabled }).toPromise()
    console.debug(res)
    return res
  }

  async postAddSchedulePeriod(req: ChargingSchedulePeriod) {
    console.debug('posting /add-schedule-period', req)
    const res = await this.http.post(`${API_URL}/add-schedule-period`, req).toPromise()
    console.debug(res)
    return res
  }

  async postUpdateSchedulePeriod(req: ChargingSchedulePeriod) {
    console.debug('posting /update-schedule-period', req)
    const res = await this.http.post(`${API_URL}/update-schedule-period`, req).toPromise()
    console.debug(res)
    return res
  }

  async postDeleteSchedulePeriod(id: string) {
    console.debug('posting /delete-schedule-period', id)
    const res = await this.http.post(`${API_URL}/delete-schedule-period`, { id }).toPromise()
    console.debug(res)
    return res
  }

  async postStartFreeTrial(req: StartFreeTrialRequest) {
    console.debug('posting /start-free-trial', req)
    const res = await this.http.post(`${API_URL}/start-free-trial`, req).toPromise()
    console.debug(res)
    return res
  }

  async postChangeSubscriptionInterval(req: ChangeSubscriptionIntervalRequest) {
    console.debug('posting /change-subscription-interval', req)
    const res = await this.http.post(`${API_URL}/change-subscription-interval`, req).toPromise()
    console.debug(res)
    return res
  }

  // async postSubscribePaidPlan() {
  //   console.debug('posting /subscribe-paid-plan')
  //   const res = await this.http.post(`${API_URL}/subscribe-paid-plan`, {}).toPromise()
  //   console.debug(res)
  //   return res
  // }

  async postCancelSubscription() {
    console.debug('posting /cancel-subscription')
    const res = await this.http.post(`${API_URL}/cancel-subscription`, {}).toPromise()
    console.debug(res)
    return res as ReplyBase
  }

  async postCreateCheckoutLink(req: CreateCheckoutLinkRequest) {
    console.debug('posting /create-stripe-checkout-link', req)
    const res = await this.http.post(`${API_URL}/create-stripe-checkout-link`, req).toPromise()
    console.debug(res)
    return res as CreateCheckoutLinkReply
  }

  async postCreateStripePortalLink() {
    console.debug('posting /create-stripe-portal-link')
    const res = await this.http.post(`${API_URL}/create-stripe-portal-link`, {}).toPromise()
    console.debug(res)
    return res as CreateStripeLinkReply
  }

  async postUpdateSetPoint(setPointAmps: number) {
    console.debug('posting /update-set-point', setPointAmps);
    const res = await this.http.post(`${API_URL}/update-set-point`, { setPointAmps }).toPromise();
    console.debug(res);
    return res
  }

  async postAmberApiToken(apiToken: string) {
    const payload: UpdateAmberApiTokenRequest = {
      apiToken: apiToken
    }
    console.debug('posting /site-settings/amber-token', payload)
    const res = await this.http.post(`${API_URL}/site-settings/amber-token`, payload).toPromise()
  }

  async deleteAmberApiToken() {
    console.debug('deleting /site-settings/amber-token')
    const res = await this.http.delete(`${API_URL}/site-settings/amber-token`).toPromise()
  }

  async postPollSolar() {
    console.debug('polling solar')
    const res = await this.http.post(`${API_URL}/poll-solar`, {}).toPromise()
    return res
  }

  async postTeslaTriggerPoll() {
    console.debug('polling tesla')
    const res = await this.http.post(`${API_URL}/tesla/worker/poll`, {}).toPromise()
    return res
  }

  async postTeslaResumePolling() {
    console.debug('posting /tesla/worker/resume-polling')
    const res = await this.http.post(`${API_URL}/tesla/worker/resume-polling`, {}).toPromise()
    return res
  }

  async postTeslaSetChargeLimit(chargeLimit: number) {
    console.debug('posting /tesla/set-charge-limit')
    const body = { chargeLimit }
    const res = await this.http.post(`${API_URL}/tesla/set-charge-limit`, body).toPromise();
    console.debug(res);
    return res;
  }

  async postTeslaWakeUp() {
    console.debug('posting /tesla/wake-up')
    const res = await this.http.post(`${API_URL}/tesla/wake-up`, {}).toPromise();
    console.debug(res);
    return res;
  }

  async postUpdateFcmToken(token: string) {
    console.debug('posting /fcm/update-token')
    const res = await this.http.post(`${API_URL}/fcm/update-token`, { token }).toPromise()
    console.debug(res)
    return res
  }

  async postReportFcmAction(req: FcmActionReport) {
    console.debug('posting /fcm/report-action')
    const res = await this.http.post(`${API_URL}/fcm/report-action`, req).toPromise()
    return res
  }

  async getDataDownloadPage(): Promise<DataDownloadPageReply> {
    const res = await this.http.get(`${API_URL}/stats/data-download-page`).toPromise()
    console.debug(res)
    return res as DataDownloadPageReply
  }

  async postDownloadData(req: DownloadDataRequest) {
    const res = await this.http.post(`${API_URL}/stats/download-data`, req).toPromise()
    return res
  }

  async getStatsSummary(): Promise<StatsSummaryReply> {
    const res = await this.http.get(`${API_URL}/stats/summary`).toPromise()
    console.debug(res)
    return res as StatsSummaryReply
  }

  async postStatsChart(req: StatsViewRequest): Promise<StatsViewReply> {
    const res = await this.http.post(`${API_URL}/stats/chart`, req).toPromise()
    //console.debug(res)
    return res as StatsViewReply
  }

  async getNoticeDetails(noticeId: string) {
    const res = await this.http.get(`${API_URL}/notice-details/${noticeId}`).toPromise()
    return res as NoticeDetailsReply
  }

  async acceptNotice(noticeId: string) {
    const res = await this.http.post(`${API_URL}/accept-notice/${noticeId}`, {}).toPromise()
    return res as AcceptNoticeReply
  }

  async postNamedAction(namedAction: NamedAction) {
    console.debug('posting named-action', namedAction)
    const res = await this.http.post(`${API_URL}/named-action/${namedAction}`, {}).toPromise()
    return res as ReplyBase
  }

  async showErrorAlert(err: Error) {
    console.debug('showErrorAlert', err)
    let message = 'Unknown system error. Please try again.'
    if (err instanceof HttpErrorResponse && err.status == 400) {
      message = err.error.message
    }
    else if (err instanceof TimeoutError) {
      message = 'Network timeout. Please try again.'
    }
    else if (err.message) {
      message = err.message
    }
    const alert = await this.alertController.create({
      header: 'Error',
      message,
      buttons: ['Close'],
    })
    await alert.present()
  }

  async showSuccessAlert(message: string) {
    const alert = await this.alertController.create({
      header: 'Success',
      message,
      buttons: ['Close'],
    })
    await alert.present()
  }

  async showReplyAlert(reply: ReplyBase, defaultHeader: string, defaultMessage: string) {
    const alert = await this.alertController.create({
      header: reply.header || defaultHeader,
      message: reply.message || defaultMessage,
      buttons: ['Close'],
    })
    await alert.present()
  }

  get isVehicleOnline(): boolean {
    return this.siteState?.teslaState?.connection_state == ConnectionState.Online
  }

  get isVehicleWakingUp(): boolean {
    return this._isVehicleWakingUp
  }

  async wakeVehicle() {
    if (this.siteState?.teslaState && !this.isVehicleOnline && !this._isVehicleWakingUp) {
      try {
        console.debug('waking vehicle')
        await this.postTeslaWakeUp()
        this._isVehicleWakingUp = true
        // waking up seems to take between 10 and 20 seconds, so timeout after 30 seconds
        // the API will poll the vehicle after 25 seconds so we can expect a feed then
        setTimeout(() => {
          if (!this.isVehicleOnline) {
            console.debug('vehicle failed to wake up')
            //this.showErrorAlert('Error', 'Failed to wake up vehicle.')
          }
          this._isVehicleWakingUp = false
        }, 30000)
      }
      catch (err) {
        await this.showErrorAlert(err)
      }
    }
  }
}
